import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Settings, Globe, Bell, Shield, Database, Loader2, Save } from "lucide-react";
import { toast } from "sonner";

interface SettingItem {
  id: string;
  key: string;
  value: any;
  description: string | null;
}

const AdminSettings = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [saving, setSaving] = useState(false);
  const [localSettings, setLocalSettings] = useState<Record<string, any>>({});

  const { data: settings, isLoading } = useQuery({
    queryKey: ["system-settings"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("system_settings")
        .select("*")
        .order("key");
      if (error) throw error;
      return data as SettingItem[];
    },
  });

  useEffect(() => {
    if (settings) {
      const map: Record<string, any> = {};
      settings.forEach((s) => { map[s.key] = s.value; });
      setLocalSettings(map);
    }
  }, [settings]);

  const updateSetting = (key: string, value: any) => {
    setLocalSettings((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    let hasError = false;

    for (const [key, value] of Object.entries(localSettings)) {
      const { error } = await supabase
        .from("system_settings")
        .update({ value: JSON.parse(JSON.stringify(value)), updated_by: user?.id } as any)
        .eq("key", key);
      if (error) {
        toast.error(`خطأ في حفظ ${key}: ${error.message}`);
        hasError = true;
      }
    }

    setSaving(false);
    if (!hasError) {
      toast.success("تم حفظ الإعدادات بنجاح ✅");
      queryClient.invalidateQueries({ queryKey: ["system-settings"] });
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl" dir="rtl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">إعدادات النظام</h1>
          <p className="text-muted-foreground text-sm">التحكم في الإعدادات العامة للمنصة</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-gradient-to-l from-amber-500 to-orange-600 border-0 text-white">
          {saving ? <Loader2 className="w-4 h-4 animate-spin ml-2" /> : <Save className="w-4 h-4 ml-2" />}
          حفظ الإعدادات
        </Button>
      </div>

      {/* Platform Info */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Globe className="w-5 h-5 text-primary" />
            معلومات المنصة
          </CardTitle>
          <CardDescription>الإعدادات الأساسية للمنصة</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>اسم المنصة</Label>
              <Input
                value={typeof localSettings.platform_name === "string" ? localSettings.platform_name : ""}
                onChange={(e) => updateSetting("platform_name", e.target.value)}
              />
            </div>
            <div>
              <Label>اللغة الافتراضية</Label>
              <Select
                value={typeof localSettings.default_language === "string" ? localSettings.default_language : "ar"}
                onValueChange={(v) => updateSetting("default_language", v)}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ar">العربية</SelectItem>
                  <SelectItem value="en">English</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Notifications */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Bell className="w-5 h-5 text-amber-500" />
            الإشعارات
          </CardTitle>
          <CardDescription>التحكم في إعدادات الإشعارات</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between rounded-lg border p-4">
            <div>
              <p className="font-medium">تفعيل الإشعارات</p>
              <p className="text-sm text-muted-foreground">إرسال الإشعارات لجميع المستخدمين</p>
            </div>
            <Switch
              checked={localSettings.notifications_enabled === true || localSettings.notifications_enabled === "true"}
              onCheckedChange={(v) => updateSetting("notifications_enabled", v)}
            />
          </div>
        </CardContent>
      </Card>

      {/* System */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Shield className="w-5 h-5 text-red-500" />
            النظام والصيانة
          </CardTitle>
          <CardDescription>إعدادات الصيانة والحماية</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between rounded-lg border p-4 border-red-200 bg-red-50/50 dark:border-red-900/30 dark:bg-red-950/20">
            <div>
              <p className="font-medium text-red-700 dark:text-red-400">وضع الصيانة</p>
              <p className="text-sm text-muted-foreground">عند التفعيل سيظهر للمستخدمين رسالة صيانة</p>
            </div>
            <Switch
              checked={localSettings.maintenance_mode === true || localSettings.maintenance_mode === "true"}
              onCheckedChange={(v) => updateSetting("maintenance_mode", v)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Subscription Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Database className="w-5 h-5 text-blue-500" />
            إعدادات الاشتراكات والملفات
          </CardTitle>
          <CardDescription>التحكم في الإعدادات المتعلقة بالاشتراكات والتحميلات</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>مدة الفترة التجريبية (بالأيام)</Label>
              <Input
                type="number"
                value={localSettings.trial_days || 15}
                onChange={(e) => updateSetting("trial_days", parseInt(e.target.value) || 15)}
                min={1}
                max={90}
                dir="ltr"
              />
            </div>
            <div>
              <Label>الحد الأقصى لحجم الملفات (MB)</Label>
              <Input
                type="number"
                value={localSettings.max_file_size_mb || 200}
                onChange={(e) => updateSetting("max_file_size_mb", parseInt(e.target.value) || 200)}
                min={10}
                max={500}
                dir="ltr"
              />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminSettings;
